import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from '../hooks/useAuth'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import logo from '../assets/logo.png'
import MfaChallenge from '../components/auth/MfaChallenge'

interface LoginForm {
  username: string
  password: string
}

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {
    login,
    verifyMfa,
    requestEmailMfaCode,
    startPendingTotpSetup,
    activatePendingTotpSetup,
    mfaPending,
    isLoggingIn,
    isAuthenticated
  } = useAuth()
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>()
  const registeredDevicesRef = useRef<Set<string>>(new Set())

  // Check for device ID in URL parameters
  useEffect(() => {
    const deviceId = searchParams.get('GeraeteID')
    if (deviceId) {
      console.log('🔍 Device ID found in URL:', deviceId)
      
      // If user is already authenticated, register device immediately
      if (isAuthenticated && !registeredDevicesRef.current.has(deviceId)) {
        console.log('🔧 User already authenticated, registering device immediately:', deviceId)
        registeredDevicesRef.current.add(deviceId)
        autoRegisterDevice(deviceId)
      } else {
        // Store device ID in session storage for later use after login
        sessionStorage.setItem('pendingDeviceId', deviceId)
      }
    }
  }, [searchParams, isAuthenticated])

  // Function to auto-register device after successful login
  const autoRegisterDevice = async (deviceId: string) => {
    try {
      console.log('🔧 Auto-registering device:', deviceId)
      const response = await api.post('/nfc-daemons/auto-register', { deviceCode: deviceId })
      
      if (response.data.success) {
        const { message, ownershipTransferred, previousOwner } = response.data
        
        if (ownershipTransferred && previousOwner) {
          toast.success(`✅ ${message}`, {
            duration: 5000,
            icon: '🔄'
          })
          console.log(`✅ Device ownership transferred from ${previousOwner} to current user`)
        } else {
          toast.success(`✅ ${message}`)
          console.log('✅ Device auto-registered successfully')
        }
        
        // Remove the device ID from URL parameters after successful registration
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('GeraeteID')
        const newUrl = `${window.location.pathname}${newSearchParams.toString() ? '?' + newSearchParams.toString() : ''}`
        navigate(newUrl, { replace: true })
      }
    } catch (error: any) {
      console.error('❌ Error auto-registering device:', error)
      const message = error.response?.data?.message || 'Fehler bei der automatischen Geräteregistrierung'
      toast.error(message)
      // Remove device from processed set on error to allow retry
      registeredDevicesRef.current.delete(deviceId)
    }
  }

  // Override the default login success behavior to handle device registration
  const handleLogin = async (data: LoginForm) => {
    try {
      // Call the original login function
      const result = await login(data)
      if (result.mfa_required) {
        return
      }
      
      // Check if there's a pending device ID to register
      const pendingDeviceId = sessionStorage.getItem('pendingDeviceId')
      if (pendingDeviceId && !registeredDevicesRef.current.has(pendingDeviceId)) {
        console.log('🔧 Processing pending device registration after login:', pendingDeviceId)
        // Mark device as being processed to prevent duplicate registrations
        registeredDevicesRef.current.add(pendingDeviceId)
        // Wait a bit for the auth state to settle
        setTimeout(() => {
          autoRegisterDevice(pendingDeviceId)
          // Clear the pending device ID
          sessionStorage.removeItem('pendingDeviceId')
        }, 1000)
      }
    } catch (error) {
      console.error('❌ Login failed:', error)
    }
  }

  const handleMfaSubmit = async (payload: { method: 'totp' | 'email'; code: string; trustDevice: boolean; deviceLabel?: string }) => {
    await verifyMfa(payload)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex items-center justify-center">
            <img src={logo} alt="Lernrakete Logo" className="h-80 w-auto" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Lernrakete 2
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Anmeldung erforderlich
          </p>
          {searchParams.get('GeraeteID') && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm text-blue-700 text-center">
                📱 NFC-Gerät wird nach der Anmeldung automatisch registriert
              </p>
            </div>
          )}
        </div>
        {mfaPending ? (
          <div className="mt-8">
            <MfaChallenge
              methods={mfaPending.methods}
              emailMask={mfaPending.emailMask}
              isLoading={isLoggingIn}
              onSubmit={handleMfaSubmit}
              onRequestEmailCode={requestEmailMfaCode}
              onStartTotpSetup={startPendingTotpSetup}
              onActivateTotpSetup={activatePendingTotpSetup}
            />
          </div>
        ) : (
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(handleLogin)}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                Benutzername
              </label>
              <input
                {...register('username', { required: 'Benutzername ist erforderlich' })}
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Benutzername"
              />
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
              )}
            </div>
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Passwort
              </label>
              <input
                {...register('password', { required: 'Passwort ist erforderlich' })}
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm pr-10"
                placeholder="Passwort"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-400" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-400" />
                )}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingIn ? 'Anmeldung läuft...' : 'Anmelden'}
            </button>
          </div>
          <div>
            <a
              href="/display"
              className="block w-full rounded-md border border-gray-300 bg-white py-2 px-4 text-center text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Display öffnen (ohne Anmeldung)
            </a>
          </div>
        </form>
        )}
      </div>
    </div>
  )
}
